<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      class="practitioner-intern__page-header"
      :page="pageHeaderConfig"
    />

    <filter-manager
      ref="filterComponent"
      v-model="selectedFilters"
      class="mb-4 practitioner-intern__filter-manager"
      :filters="filtersConfig"
      @search="search(1)"
    />

    <separator class="pt-3 my-4" text="Resultado de Practicantes e Internos" />

    <result-not-found v-if="pagination.totalItemCount === 0" />

    <row
      v-for="result in pagination.data"
      :key="result.id"
      class="mb-3"
      :selected-academic-year="selectedFilters.academicYear.id"
      :value="result"
    />

    <pagination
      v-if="pagination.totalItemCount > paginationPageMaxSize"
      :data="pagination"
      @pagination-go-page="setCurrentPage"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import ResultNotFound from 'custom-result-not-found';
import Pagination from '@/components/common/Pagination';
import Separator from 'custom-separator';
import Row from './Row';
import storeMixin from 'utils/mixins/storeGenerator';
import alertMixin from 'utils/mixins/alert.mixin';

import {
  initPractitionerInternFilters as _initPractitionerInternFilters,
  getServiceTypes as _getServiceTypes,
  getServices as _getServices,
  getPractitioners as _getPractitioners,
  getPractitionersByCompany as _getPractitionersByCompany,
} from '@/modules/practitionerIntern/api';

import { getProvidersByAcademicyearId as _getProvidersByAcademicyearId } from '@/modules/shared/provider.api';

const PRACTITIONER_INTERN_ADD = 'PractitionerInternAdd';
const PRACTITIONER_INTERN_EDIT = 'PractitionerInternEdit';
const STORE_MODULE_NAME = 'practitionerModule';

export default {
  name: 'PractitionerIntern',
  components: {
    PageHeader,
    FilterManager,
    ResultNotFound,
    Pagination,
    Separator,
    Row,
  },
  mixins: [alertMixin, storeMixin],
  data: () => ({
    moduleName: STORE_MODULE_NAME,
    results: [],
    pagination: {
      totalItemCount: 0,
      count: 0,
      pageCount: 0,
      currentPage: 0,
      data: [],
    },
    pageSize: 15,
    filterCollections: {
      academicYears: [],
      practitionerTypes: [],
      statusList: [],
      serviceTypes: [],
      disciplineTypes: [],
      companies: [],
      practitioners: [],
    },
    selectedFilters: {
      academicYear: null,
      practitioner: null,
      practitionerType: null,
      serviceType: null,
      disciplineType: null,
      status: null,
      company: null,
    },
  }),
  computed: {
    ...mapState('$_user', { user: 'user', providers: 'companies' }),
    paginationPageMaxSize() {
      return 15;
    },
    pageHeaderConfig() {
      return {
        title: 'Practicantes e Internos',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Practicantes e Internos'],
      };
    },
    isAdminProvider() {
      return this.userRole == 'AdminProvider';
    },
    isVisible() {
      return (
        this.userHasPermissions('provider.practitioner.add') &&
        (this.isAdminProvider || this.providers.length === 1)
      );
    },
    headerActionConfig() {
      return [
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus',
          variant: 'success float-right',
          click: this.addHandler,
          visible: this.isVisible,
        },
      ];
    },
    filtersConfig() {
      let result = [];

      let academicYearFilter = {
        component: 'CustomDropDown',
        name: 'academicYear',
        initialValue: this.currentAcademicYear(),
        options: this.filterCollections.academicYears,
        placeholder: 'Seleccione el Año',
        fieldName: 'description',
        fieldKey: 'id',
        class: 'col-sm-12 col-md-4 col-lg-2',
        clearable: false,
        onChanged: this.onAcademicYearChanged,
      };

      result.push(academicYearFilter);

      let practitionerFilter = {
        component: 'CustomDropDown',
        name: 'practitioner',
        options: this.filterCollections.practitioners,
        fieldName: 'name',
        fieldKey: 'id',
        placeholder: 'Seleccione Nombre del Practicante e Interno',
        class: 'col-sm-12 col-md-4 col-lg-6',
        icon: 'fa fa-search orange-icon',
        leftIcon: true,
      };

      if (this.user.profile.companyid === undefined) {
        let companyFilter = {
          name: 'company',
          component: 'CustomDropDown',
          options: this.filterCollections.companies,
          fieldName: 'name',
          fieldKey: 'id',
          placeholder: 'Seleccione la Compañía',
          class: 'col-sm-12 col-md-4 col-lg-4',
          onChanged: this.onCompanyChanged,
          validations: 'required',
          error: 'La compañía es requerida. (R180006)',
        };

        result.push(companyFilter);
      } else {
        academicYearFilter.class = 'col-sm-12 col-md-3';
        practitionerFilter.class = 'col-sm-12 col-md-9';
      }

      result.push(practitionerFilter);

      result.push({
        component: 'CustomDropDown',
        name: 'type',
        label: 'Tipo:',
        options: this.filterCollections.practitionerTypes,
        placeholder: 'Seleccione',
        fieldKey: 'id',
        fieldName: 'name',
        class: 'col-sm-12 col-md-6 col-lg-4',
        isAdvanced: true,
      });

      result.push({
        name: 'serviceType',
        component: 'CustomDropDown',
        options: this.filterCollections.serviceTypes,
        fieldName: 'name',
        fieldKey: 'id',
        label: 'Tipo de Servicio:',
        isAdvanced: true,
        class: 'col-sm-12 col-md-6 col-lg-4',
        placeholder: 'Seleccione',
        onChanged: this.onServiceTypeChanged,
      });

      result.push({
        name: 'disciplineType',
        component: 'CustomDropDown',
        options: this.filterCollections.disciplineTypes,
        fieldName: 'name',
        fieldKey: 'id',
        label: 'Servicio:',
        isAdvanced: true,
        class: 'col-sm-12 col-md-6 col-lg-4',
        placeholder: 'Seleccione',
      });

      result.push({
        component: 'CustomDropDown',
        name: 'status',
        label: 'Estatus:',
        options: this.filterCollections.statusList,
        placeholder: 'Seleccione',
        fieldKey: 'id',
        fieldName: 'name',
        class: 'col-sm-12 col-md-6 col-lg-4',
        isAdvanced: true,
      });

      return result;
    },
  },
  async created() {
    await this.recoveryData();
    await this.initFilters();
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async initFilters() {
      const { data: filterResponse } = await _initPractitionerInternFilters();

      this.filterCollections = {
        ...filterResponse.collections,
        serviceTypes:
          filterResponse.collections.serviceTypes ||
          this.filterCollections.serviceTypes,
        disciplineTypes:
          filterResponse.collections.disciplineTypes.length > 0
            ? filterResponse.collections.disciplineTypes
            : this.filterCollections.disciplineTypes,
        practitioners:
          filterResponse.collections.practitioners ||
          this.filterCollections.practitioners,
      };

      this.selectedFilters = {
        academicYear: filterResponse.filters.academicYear,
        company: filterResponse.filters.company,
        status: filterResponse.filters.status,
        serviceType: this.selectedFilters.serviceType,
        disciplineType: this.selectedFilters.disciplineType,
        practitioner: this.selectedFilters.practitioner,
        ...this.selectedFilters,
      };
    },
    async search(pageNumber) {
      let model = {
        fiscalYearId: this.selectedFilters.academicYear
          ? this.selectedFilters.academicYear.id
          : null,
        companyId:
          this.user.profile.companyid != undefined
            ? this.user.profile.companyid
            : this.selectedFilters.company.id,
        practitionerId: this.selectedFilters.practitioner?.id ?? null,
        practitionerTypeId: this.selectedFilters.type
          ? this.selectedFilters.type.id
          : null,
        serviceTypeId: this.selectedFilters.serviceType
          ? this.selectedFilters.serviceType.id
          : null,
        serviceId: this.selectedFilters.disciplineType
          ? this.selectedFilters.disciplineType.id
          : null,
        statusId: this.selectedFilters.status
          ? this.selectedFilters.status.id
          : null,
        page: pageNumber,
        pageSize: this.pageSize,
      };

      await _getPractitioners(model).then(({ data }) => {
        this.pagination = {
          ...data,
          count: this.paginationPageMaxSize,
        };
      });
    },
    setCurrentPage(pageNumber) {
      this.search(pageNumber);
    },
    async addHandler() {
      if (this.isAdminProvider) {
        const isValid = await this.$refs.filterComponent.$refs.formValidator.validate();

        if (!isValid) {
          this.Toast('Alerta', 'Es requerido seleccionar Proveedor.', 'error');
          return;
        }
      }

      this.$router.push({
        name: PRACTITIONER_INTERN_ADD,
      });
    },
    enterHandler() {
      this.$router.push({
        name: PRACTITIONER_INTERN_EDIT,
      });
    },
    async onAcademicYearChanged() {
      this.selectedFilters.practitioner = null;
      this.selectedFilters.company = null;
      this.selectedFilters.serviceType = null;
      this.selectedFilters.disciplineType = null;

      await _getProvidersByAcademicyearId(
        this.selectedFilters.academicYear.id
      ).then(async ({ data }) => {
        this.filterCollections.companies = data;

        await this.fetchServiceTypes();
      });
    },
    async onCompanyChanged() {
      this.selectedFilters.serviceType = null;
      this.selectedFilters.disciplineType = null;

      await _getPractitionersByCompany(
        this.selectedFilters.academicYear.id,
        this.selectedFilters.company.id
      ).then(async ({ data }) => {
        this.filterCollections.practitioners = data;

        await this.fetchServiceTypes();
      });
    },
    async onServiceTypeChanged() {
      this.selectedFilters.disciplineType = null;

      await this.fetchServices();
    },
    async fetchServiceTypes() {
      this.filterCollections.serviceTypes = [];
      this.filterCollections.disciplineTypes = [];

      await _getServiceTypes(
        this.selectedFilters.academicYear.id,
        this.selectedFilters.company?.id || this.user.profile.companyId
      ).then(async ({ data }) => {
        this.filterCollections.serviceTypes = data;
      });
    },
    async fetchServices() {
      this.filterCollections.disciplineTypes = [];

      await _getServices(
        this.selectedFilters.academicYear.id,
        this.selectedFilters.company?.id || this.user.profile.companyId,
        this.selectedFilters.serviceType != null
          ? this.selectedFilters.serviceType.id
          : null
      ).then(async ({ data }) => {
        this.filterCollections.disciplineTypes = data;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.actions > div {
  text-align: center;
}
.practitioner-intern {
  &__filter-manager ::v-deep {
    .ab-btn.success {
      margin-right: 20px;
      width: 86%;
    }
  }
  &__page-header ::v-deep {
    @media only screen and (max-width: 768px) {
      .actions {
        display: flex !important;
      }
      .ab-btn.success {
        width: auto;
      }
    }
  }
}
</style>
